import { useCallback } from "react";

export default function useAccessManagement({
  accessControlService,
  isAdmin,
  isAccessControlAvailable,
  setIsAccessAdmin,
  setIsAccessControlAvailable,
  setAccessRecord,
  setAccessStatus,
  setAccessRequests,
  setIsAccessRequestsLoading,
  sessionUserId,
  showStatus,
}) {
  const syncOwnAccessRequest = useCallback(async (currentSession) => {
    const result = await accessControlService.syncOwnAccessRequest({
      currentSession,
      isAccessControlAvailable,
    });

    if (result.error) {
      console.error("Fehler beim Prüfen der Zugriffsfreigabe:", result.error);
    }

    if (result.accessControlAvailable === false && isAccessControlAvailable) {
      setIsAccessControlAvailable(false);
    }

    setAccessRecord(result.record);
    setAccessStatus(result.status);
    setIsAccessAdmin(!!result.isAdmin);
    return { status: result.status, record: result.record, isAdmin: !!result.isAdmin };
  }, [accessControlService, isAccessControlAvailable, setAccessRecord, setAccessStatus, setIsAccessAdmin, setIsAccessControlAvailable]);

  const loadAccessRequests = useCallback(async () => {
    if (!isAdmin || !isAccessControlAvailable) return;
    setIsAccessRequestsLoading(true);
    try {
      const result = await accessControlService.loadAccessRequests({
        isAdmin,
        isAccessControlAvailable,
      });

      if (result.accessControlAvailable === false) {
        setIsAccessControlAvailable(false);
        setAccessRequests([]);
        return;
      }

      if (!result.ok) {
        throw result.error;
      }

      setAccessRequests(result.requests);
    } catch (error) {
      console.error("Fehler beim Laden der Freigaben:", error);
      showStatus("Fehler beim Laden der Freigaben.", true);
    } finally {
      setIsAccessRequestsLoading(false);
    }
  }, [accessControlService, isAccessControlAvailable, isAdmin, setAccessRequests, setIsAccessControlAvailable, setIsAccessRequestsLoading, showStatus]);

  const updateAccessRequestStatus = useCallback(async (entry, nextStatus) => {
    if (!isAdmin || !entry?.user_id) return;

    const result = await accessControlService.updateAccessRequestStatus({
      entry,
      nextStatus,
      sessionUserId,
    });

    if (!result.ok) {
      console.error("Fehler beim Aktualisieren der Freigabe:", result.error);
      showStatus("Freigabe konnte nicht aktualisiert werden.", true);
      return;
    }

    if (entry.user_id === sessionUserId) {
      setAccessStatus(nextStatus);
    }

    if (nextStatus === "approved" && entry?.email) {
      try {
        await accessControlService.sendAccessApprovedMail(entry);
      } catch (mailError) {
        console.error("Freigabe-E-Mail konnte nicht gesendet werden:", mailError);
      }
    }

    await loadAccessRequests();
    showStatus(nextStatus === "approved" ? "Benutzer freigegeben ✓" : "Benutzer abgelehnt ✓");
  }, [accessControlService, isAdmin, loadAccessRequests, sessionUserId, setAccessStatus, showStatus]);

  const resendAccessApprovedMail = useCallback(async (entry) => {
    if (!entry?.email) {
      showStatus("E-Mail-Adresse fehlt.", true);
      return;
    }
    try {
      await accessControlService.sendAccessApprovedMail(entry);
      showStatus("Freigabe-E-Mail erneut gesendet ✓");
    } catch (error) {
      console.error("Fehler beim erneuten E-Mail Versand:", error);
      showStatus("Freigabe-E-Mail konnte nicht gesendet werden.", true);
    }
  }, [accessControlService, showStatus]);

  return {
    syncOwnAccessRequest,
    loadAccessRequests,
    updateAccessRequestStatus,
    resendAccessApprovedMail,
  };
}
